import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CELL } from "@/lib/people";

const LETTERS = ["M", "T", "W", "T", "F", "S", "S"];

function Cell({ c, date, name }) {
  const meta = CELL[c.state] || CELL.unknown;
  const coloured = c.state === "off" || c.state === "part";
  const title = `${name} · ${dayjs(date).format("ddd D MMM")}: ${c.state === "holiday" ? c.label : coloured ? `${c.label}${c.state === "part" ? ` (${c.off_hours}h of ${c.hours}h)` : ""}${c.pending ? " — awaiting approval" : ""}` : meta.label}`;
  return (
    <td className="p-0.5" title={title}>
      <div className={`flex h-8 min-w-[28px] items-center justify-center rounded text-[10px] font-semibold ${meta.cls} ${c.pending ? "opacity-60 ring-1 ring-dashed ring-[#0B1729]/40" : ""}`}
        style={coloured ? { background: c.state === "part" ? `linear-gradient(135deg, ${c.colour} 50%, ${c.colour}33 50%)` : c.colour } : undefined}
        data-testid={`cal-${date}`}>
        {c.state === "off" ? "Off" : c.state === "part" ? `${c.off_hours}h` : c.state === "holiday" ? "PH" : ""}
      </div>
    </td>
  );
}

export function AvailabilityCalendar({ orgId }) {
  const [month, setMonth] = useState(null);
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/workspace/leave/calendar?org_id=${orgId}${month ? `&month=${month}` : ""}`).then((r) => setD(r.data));
  }, [orgId, month]);
  if (!d) return <Icons.Loader2 className="mx-auto mt-8 animate-spin text-[#FFB000]" />;
  const shift = (n) => setMonth(dayjs(`${d.month}-01`).add(n, "month").format("YYYY-MM"));

  return (
    <div data-testid="availability-calendar">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => shift(-1)} data-testid="cal-prev"><Icons.ChevronLeft size={16} /></Button>
        <div className="min-w-[150px] text-center font-display text-base font-bold text-[#0B1729]" data-testid="cal-month">{dayjs(`${d.month}-01`).format("MMMM YYYY")}</div>
        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => shift(1)} data-testid="cal-next"><Icons.ChevronRight size={16} /></Button>
        <div className="ml-auto flex flex-wrap items-center gap-3 text-[11px] text-[#1E2D44]/60">
          {[["in", "In"], ["off", "Off"], ["part", "Part day"], ["holiday", "Public holiday"], ["non_working", "Not working"]].map(([k, l]) => (
            <span key={k} className="flex items-center gap-1.5">
              <span className={`h-3 w-3 rounded ${CELL[k].cls}`} style={k === "off" || k === "part" ? { background: "#4A78A8" } : undefined} /> {l}
            </span>
          ))}
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded opacity-60 ring-1 ring-[#0B1729]/40" style={{ background: "#4A78A8" }} /> Awaiting approval</span>
        </div>
      </div>
      <div className="amden-card overflow-x-auto p-3">
        <table className="w-full border-separate border-spacing-0 text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 min-w-[150px] bg-white pb-1 text-left font-semibold text-[#1E2D44]/50">Person</th>
              {d.days.map((x) => (
                <th key={x.date} className={`pb-1 text-center font-medium ${x.date === d.today ? "text-[#FFB000]" : x.weekday > 4 ? "text-[#1E2D44]/30" : "text-[#1E2D44]/55"}`}>
                  <div>{LETTERS[x.weekday]}</div><div>{Number(x.date.slice(8))}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.people.map((p) => (
              <tr key={p.user_id} data-testid={`cal-row-${p.user_id}`}>
                <td className="sticky left-0 z-10 bg-white py-0.5 pr-3">
                  <div className="truncate font-medium text-[#0B1729]">{p.name}</div>
                  <div className="truncate text-[10px] text-[#1E2D44]/45">{p.has_pattern ? p.title || p.department : "No work pattern set"}</div>
                </td>
                {d.days.map((x) => <Cell key={x.date} c={p.cells[x.date]} date={x.date} name={p.name} />)}
              </tr>
            ))}
            {d.people.length === 0 && <tr><td colSpan={d.days.length + 1} className="p-6 text-center text-sm text-[#1E2D44]/45">No one with an active employee profile in your team.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-[#1E2D44]/45">Built from each person's work pattern, the company's public holidays, and approved or pending leave. Sensitive absences show as "Absent".</p>
    </div>
  );
}
